import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../../../component/navbar";
import { Footer } from "../../../component/footer";
import api from "../../../api";
import { notify } from "../../../utils/toast";
import "./booking.css";

const TRIP_TYPES = [
  { value: "daily_rent", label: "Daily rent",    icon: "fa-solid fa-car" },
  { value: "wedding",    label: "Wedding",        icon: "fa-solid fa-ring" },
  { value: "picnic",     label: "Picnic / tour",  icon: "fa-solid fa-umbrella-beach" },
  { value: "party",      label: "Party / event",  icon: "fa-solid fa-champagne-glasses" },
  { value: "airport",    label: "Airport",        icon: "fa-solid fa-plane-departure" },
  { value: "other",      label: "Other",          icon: "fa-solid fa-ellipsis" },
];

const VEHICLE_TYPES = [
  { value: "car",     label: "Car",     icon: "fa-solid fa-car-side" },
  { value: "van",     label: "Van",     icon: "fa-solid fa-van-shuttle" },
  { value: "bus",     label: "Bus",     icon: "fa-solid fa-bus" },
  { value: "coaster", label: "Coaster", icon: "fa-solid fa-bus-simple" },
];

const today = new Date().toISOString().split("T")[0];

export const BookingPage = () => {
  const [selectedTrip, setSelectedTrip]       = useState("daily_rent");
  const [selectedVehicle, setSelectedVehicle] = useState("van");
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    pickupLocation: "", dropLocation: "",
    date: "", departureTime: "", returnTime: "", passengers: "", message: "",
  });
  const [loader, setLoader] = useState(false);
  const [sent, setSent]     = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const getGPS = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        () => resolve({}),
        { timeout: 5000 }
      );
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.pickupLocation || !form.date) {
      notify.error("Please fill all required fields.");
      return;
    }
    try {
      setLoader(true);
      const coords = await getGPS();
      const response = await api.addBooking({
        ...form,
        tripType: selectedTrip,
        vehicleType: selectedVehicle,
        ...coords,
      });
      if (response?.success) {
        setSent(true);
      }
    } catch (err) {
      notify.error(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className="booking-page">
      <Navbar />

      {/* Hero Banner */}
      <div className="bk-hero">
        <h1>
          <i className="fa-solid fa-calendar-check" style={{ marginRight: 14, opacity: 0.7 }}></i>
          Book Your Ride
        </h1>
        <p>Weddings, picnics, parties, airport transfers or daily rent — fill the form and we'll call you to confirm.</p>
      </div>

      <div className="bk-wrap">
        <div className="bk-card">
          {sent ? (
            <div className="bk-success">
              <div className="success-icon">✅</div>
              <h3>Booking Request Sent!</h3>
              <p>
                Thank you! Our team will call you within a few hours<br />
                to confirm your booking details.
              </p>
              <Link to="/">Back to Home</Link>
            </div>
          ) : (
            <div className="bk-card-body">

              {/* Step 1: Trip Type */}
              <div className="bk-step-label">
                <span className="bk-step-num">1</span>
                Select trip type <span className="bk-req">*</span>
              </div>
              <div className="bk-trip-grid">
                {TRIP_TYPES.map((t) => (
                  <div
                    key={t.value}
                    className={`bk-trip-card${selectedTrip === t.value ? " active" : ""}`}
                    onClick={() => setSelectedTrip(t.value)}
                  >
                    <i className={t.icon}></i>
                    {t.label}
                  </div>
                ))}
              </div>

              {/* Step 2: Vehicle Type */}
              <div className="bk-step-label">
                <span className="bk-step-num">2</span>
                Select vehicle type <span className="bk-req">*</span>
              </div>
              <div className="bk-veh-grid">
                {VEHICLE_TYPES.map((v) => (
                  <div
                    key={v.value}
                    className={`bk-veh-card${selectedVehicle === v.value ? " active" : ""}`}
                    onClick={() => setSelectedVehicle(v.value)}
                  >
                    <i className={v.icon}></i>
                    {v.label}
                  </div>
                ))}
              </div>

              <div className="bk-divider"></div>

              {/* Step 3: Trip Details */}
              <div className="bk-step-label">
                <span className="bk-step-num">3</span>
                Trip details
              </div>
              <div className="bk-frow bk-f2">
                <div className="bk-fg">
                  <label>Pickup location <span className="bk-req">*</span></label>
                  <input
                    type="text"
                    name="pickupLocation"
                    placeholder="e.g. Gulshan-e-Iqbal, Karachi"
                    value={form.pickupLocation}
                    onChange={onChange}
                  />
                </div>
                <div className="bk-fg">
                  <label>Drop location</label>
                  <input
                    type="text"
                    name="dropLocation"
                    placeholder="e.g. Hawksbay Beach"
                    value={form.dropLocation}
                    onChange={onChange}
                  />
                </div>
              </div>
              <div className="bk-frow bk-f2">
                <div className="bk-fg">
                  <label>Date <span className="bk-req">*</span></label>
                  <input
                    type="date"
                    name="date"
                    min={today}
                    value={form.date}
                    onChange={onChange}
                  />
                </div>
                <div className="bk-fg">
                  <label>Passengers</label>
                  <input
                    type="number"
                    name="passengers"
                    placeholder="e.g. 15"
                    min="1"
                    value={form.passengers}
                    onChange={onChange}
                  />
                </div>
              </div>
              <div className="bk-frow bk-f2">
                <div className="bk-fg">
                  <label>Departure time</label>
                  <input
                    type="time"
                    name="departureTime"
                    value={form.departureTime}
                    onChange={onChange}
                  />
                </div>
                <div className="bk-fg">
                  <label>Return time</label>
                  <input
                    type="time"
                    name="returnTime"
                    value={form.returnTime}
                    onChange={onChange}
                  />
                </div>
              </div>

              <div className="bk-divider"></div>

              {/* Step 4: Contact */}
              <div className="bk-step-label">
                <span className="bk-step-num">4</span>
                Your contact
              </div>
              <div className="bk-frow bk-f3">
                <div className="bk-fg">
                  <label>Full name <span className="bk-req">*</span></label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Ahsan Iqbal"
                    value={form.name}
                    onChange={onChange}
                  />
                </div>
                <div className="bk-fg">
                  <label>Phone <span className="bk-req">*</span></label>
                  <input
                    type="text"
                    name="phone"
                    placeholder="+92 300 0000000"
                    value={form.phone}
                    onChange={onChange}
                  />
                </div>
                <div className="bk-fg">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={onChange}
                  />
                </div>
              </div>
              <div className="bk-fg">
                <label>Additional notes</label>
                <textarea
                  name="message"
                  placeholder="Special requirements, decorations, route preferences, number of days..."
                  value={form.message}
                  onChange={onChange}
                />
              </div>

              <button
                className="bk-submit-btn"
                onClick={handleSubmit}
                disabled={loader}
              >
                {loader ? (
                  <><i className="fa-solid fa-spinner fa-spin"></i> Submitting...</>
                ) : (
                  <><i className="fa-solid fa-calendar-check"></i> Submit Booking Request</>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Trust bar */}
        <div className="bk-trust-bar">
          <div className="bk-trust-item">
            <i className="fa-solid fa-phone"></i> We'll call to confirm within 2 hours
          </div>
          <div className="bk-trust-item">
            <i className="fa-solid fa-shield-halved"></i> Small token advance to confirm booking
          </div>
          <div className="bk-trust-item">
            <i className="fa-solid fa-clock"></i> Available 24/7
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};
